/* eslint-disable @typescript-eslint/no-unused-vars */
import { SelectWithLabel } from '@adotel/components'
import { DateObject } from '@adotel/components/build/helpers/date'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Typography,
  Button,
  Divider,
  Chip,
  MenuItem,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@material-ui/core'
import { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { ConfirmChangeReservationModal } from '../../../../components/ConfirmCancelReservationModal'
import CustomCounter from '../../../../components/CustomCounter'
import TimePeriodForm from '../../../../components/TimePeriodForm'
import { CANCEL_RESERVATION_TA_PANEL_ROOM_STAY_PREVIEW_QUERY } from '../../../../graphql/cancelReservationTAPanelRoomStay'
import { MODIFY_RESERVE_TA_PANEL_MUTATION } from '../../../../graphql/modifyReserveTAPanel'
import { SEARCH_AVAILABILITY_QUERY } from '../../../../graphql/searchAvailability'
import {
  convertGregorianToJalali,
  getTodayDate
} from '../../../../helpers/date'
import { getTranslate, lang } from '../../../../localization'
import { RoomStay } from '../../../../types/bookingDetails'
import { CancelRoomStayInfo } from '../../../../types/cancelReservationTAPanel'
import { Currency } from '../../../../types/currency'
import {
  ModifyReservationInput,
  ModifyReserveResponse
} from '../../../../types/modifyReserveTAPanel'
import {
  AddOnForModify,
  SearchAvailabilityResponse
} from '../../../../types/searchAvailability'
import AddOn from '../Add-On'
import ProfileForm from '../Form'
import { modifyQuoteModalData } from './modifyQuoteModalData'
import { useStyles } from './styles/index.styles'

type Option = {
  roomTypeName: string
  ratePlanName: string
  roomTypeCode: string
  displayRoomTypeCode: string
  ratePlanCode: string
  displayRatePlanCode: string
  adults: number
  extraBeds: number
  availableAddOnes: AddOnForModify[]
}

type RoomInformationProps = {
  roomIndex: number
  modifyReservationInput: ModifyReservationInput
  setModifyReservationInput: React.Dispatch<
    React.SetStateAction<ModifyReservationInput>
  >
  initialRoom: RoomStay
}

const RoomStayInformation = ({
  roomIndex,
  modifyReservationInput,
  setModifyReservationInput,
  initialRoom
}: RoomInformationProps) => {
  const classes = useStyles()
  const history = useHistory()
  const params = useParams<{ reserveNumber: string }>()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const datePickerRef = useRef<HTMLDivElement | null>(null)
  const [arrivalAndDepartureSelected, setArrivalAndDepartureSelected] =
    useState<boolean>(false)

  const [dateObjectArrivalDeparture, setDateObjectArrivalDeparture] =
    useState<DateObject>({
      gregorianStartDate: modifyReservationInput.roomStays[roomIndex].checkIn,
      gregorianEndDate: modifyReservationInput.roomStays[roomIndex].checkOut,
      jalaaliStartDate: convertGregorianToJalali(
        modifyReservationInput.roomStays[roomIndex].checkIn
      ),
      jalaaliEndDate: convertGregorianToJalali(
        modifyReservationInput.roomStays[roomIndex].checkOut
      )
    })

  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalData, setModalData] = useState<{
    penalty: number
    payedPrice: number
    currentPrice?: number
    payBack: number
    currency: Currency
    promotionDropped?: boolean
  }>({ currency: 'IRR', payBack: 0, payedPrice: 0, penalty: 0 })
  const [options, setOptions] = useState<Option[]>([])
  const [selectedOption, setSelectedOption] = useState<Option>()
  const [mode, setMode] = useState<'cancelation' | 'modification'>(
    'cancelation'
  )

  const [guestCount, setGuestCount] = useState<number>(
    modifyReservationInput.roomStays[roomIndex].occupancy.adults +
      modifyReservationInput.roomStays[roomIndex].occupancy.extraBeds
  )

  const isRangeSubset = (
    initialDateRange: DateObject,
    currentDateRange: DateObject
  ): boolean => {
    if (
      !initialDateRange.gregorianStartDate ||
      !initialDateRange.gregorianEndDate ||
      !currentDateRange.gregorianStartDate ||
      !currentDateRange.gregorianEndDate
    ) {
      return false
    }

    const initialStart = new Date(initialDateRange.gregorianStartDate)
    const initialEnd = new Date(initialDateRange.gregorianEndDate)

    const currentStart = new Date(currentDateRange.gregorianStartDate)
    const currentEnd = new Date(currentDateRange.gregorianEndDate)

    return currentStart >= initialStart && currentEnd <= initialEnd
  }

  //SEARCH_AVAILIBILITY_QUERY
  const [search, { loading: loadingForSearch }] = useLazyQuery<{
    searchAvailability: SearchAvailabilityResponse
  }>(SEARCH_AVAILABILITY_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      let opts: Option[] = res.searchAvailability.entities.flatMap((entity) =>
        entity.rooms.flatMap((r) =>
          r.ratePlans.map((rp) => {
            const targetRoomStay = modifyReservationInput.roomStays.find(
              (room) =>
                room.displayRoomTypeCode === r.displayRoomTypeCode &&
                room.displayRatePlanCode === rp.displayRatePlanCode
            )

            return {
              roomTypeName: r.roomTypeName,
              ratePlanName: rp.ratePlanName,
              roomTypeCode: r.roomTypeCode,
              displayRoomTypeCode: r.displayRoomTypeCode,
              ratePlanCode: rp.ratePlanCode,
              displayRatePlanCode: rp.displayRatePlanCode,
              adults: r.occupancy.adults,
              extraBeds: r.occupancy.extraBeds,
              availableAddOnes:
                rp.price.afterDiscount.flatRateAddOns.map<AddOnForModify>(
                  (addon) => ({
                    name: addon.name,
                    code: addon.code,
                    totalAmount: addon.totalAmount,
                    currency: addon.currency,
                    included: targetRoomStay
                      ? targetRoomStay.addons.includes(addon.name)
                      : false
                  })
                )
            }
          })
        )
      )

      const isInRange = isRangeSubset(
        {
          gregorianStartDate: initialRoom.checkIn,
          gregorianEndDate: initialRoom.checkOut
        },
        dateObjectArrivalDeparture
      )

      if (!isInRange) {
        if (opts.length > 0) {
          const matchIndex = opts.findIndex(
            (option) =>
              option.displayRoomTypeCode === initialRoom.displayRoomTypeCode &&
              option.displayRatePlanCode === initialRoom.displayRatePlanCode
          )

          if (matchIndex > -1) {
            const matched = opts[matchIndex]
            opts = [matched, ...opts.filter((_, i) => i !== matchIndex)]

            setSelectedOption(matched)
          } else {
            setSelectedOption(undefined)
          }
          setOptions(opts)
        } else {
          setSelectedOption(undefined)
          setOptions([])
        }
      } else {
        //append existing room (not show in search result) but do not duplicate it!
        const existingOption: Option = {
          roomTypeName: initialRoom.roomTypeName,
          ratePlanName: initialRoom.ratePlanName,
          roomTypeCode: initialRoom.roomTypeCode,
          displayRoomTypeCode: initialRoom.displayRoomTypeCode,
          ratePlanCode: initialRoom.ratePlanCode,
          displayRatePlanCode: initialRoom.displayRatePlanCode,
          adults: initialRoom.occupancy.adults,
          extraBeds: initialRoom.occupancy.extraBeds,
          availableAddOnes: []
        }

        if (opts.length > 0) {
          const matchIndex = opts.findIndex(
            (option) =>
              option.displayRoomTypeCode === initialRoom.displayRoomTypeCode &&
              option.displayRatePlanCode === initialRoom.displayRatePlanCode
          )

          if (matchIndex > -1) {
            const matched = opts[matchIndex]
            opts = [matched, ...opts.filter((_, i) => i !== matchIndex)]

            setSelectedOption(matched)
          } else {
            setSelectedOption(undefined)
          }

          const existsInOpts = opts.some(
            (o) =>
              o.displayRoomTypeCode === existingOption.displayRoomTypeCode &&
              o.displayRatePlanCode === existingOption.displayRatePlanCode
          )

          const finalOpts = existsInOpts ? opts : [existingOption, ...opts]

          setOptions(finalOpts)
        } else {
          setSelectedOption(existingOption)
          setOptions([existingOption])
        }
      }
    }
  })

  //CANCEl_ROOMSTAY_PREVIEW
  const [cancelRoomStayPreview, { loading: loadingForCancelRoomStayPreview }] =
    useLazyQuery<{
      cancelReservationTAPanelRoomStayPreview: CancelRoomStayInfo
    }>(CANCEL_RESERVATION_TA_PANEL_ROOM_STAY_PREVIEW_QUERY, {
      onCompleted: (res) => {
        const data = res.cancelReservationTAPanelRoomStayPreview
        const penalty = data.penalty
        const payedPrice = data.oldPrice
        const payBack = payedPrice - penalty
        const currency = data.currency
        setModalData({ penalty, payedPrice, payBack, currency })
        setMode('cancelation')
        setModalOpen(true)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    })

  //MODIFY_RESERVE_TA_PANEL_MUTATION
  const [modifyReserve, { loading: loadingForModifyReserve }] = useMutation<{
    modifyReserveTAPanel: ModifyReserveResponse
  }>(MODIFY_RESERVE_TA_PANEL_MUTATION, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setMode('modification')
      setModalData(modifyQuoteModalData(res.modifyReserveTAPanel.info))
      setModalOpen(true)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  useEffect(() => {
    if (
      dateObjectArrivalDeparture?.gregorianStartDate &&
      dateObjectArrivalDeparture?.gregorianEndDate
    ) {
      search({
        variables: {
          input: {
            propertyCode:
              modifyReservationInput.roomStays[roomIndex].propertyCode,
            channelId: modifyReservationInput.roomStays[roomIndex].channelID,
            arrival: dateObjectArrivalDeparture.gregorianStartDate,
            departure: dateObjectArrivalDeparture.gregorianEndDate,
            currency: modifyReservationInput.roomStays[roomIndex].currency,
            guestsAges: Array(guestCount).fill(24),
            sort: 'PRICE_DESC',
            showUnavailableRatePlans: false
          }
        }
      })
    }
  }, [dateObjectArrivalDeparture, guestCount])

  useEffect(() => {
    if (selectedOption) {
      setModifyReservationInput({
        ...modifyReservationInput,
        roomStays: [
          ...modifyReservationInput.roomStays.slice(0, roomIndex),
          {
            ...modifyReservationInput.roomStays[roomIndex],
            roomTypeCode: selectedOption?.roomTypeCode ?? '',
            ratePlanCode: selectedOption?.ratePlanCode ?? '',
            occupancy: {
              adults: guestCount,
              extraBeds: selectedOption.extraBeds,
              children: { child1: 0, child2: 0, child3: 0, child4: 0 }
            },
            checkIn: dateObjectArrivalDeparture.gregorianStartDate ?? '',
            checkOut: dateObjectArrivalDeparture.gregorianEndDate ?? ''
          },
          ...modifyReservationInput.roomStays.slice(roomIndex + 1)
        ]
      })
    }
  }, [selectedOption, dateObjectArrivalDeparture, guestCount])

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef?.current?.contains(event.target as Node) &&
        arrivalAndDepartureSelected
      ) {
        setArrivalAndDepartureSelected(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerRef, arrivalAndDepartureSelected])

  return (
    <>
      <div className={classes.root}>
        <div className={classes.top}>
          <Typography variant="h4" className={classes.header}>
            {getTranslate('Guest Information')}
          </Typography>
          <div className={classes.btnContainer}>
            <Button
              variant="outlined"
              color="primary"
              disabled={
                !modifyReservationInput.roomStays[roomIndex].canCancel ||
                loadingForCancelRoomStayPreview
              }
              className={classes.btn}
              onClick={() => {
                cancelRoomStayPreview({
                  variables: {
                    reservationNumber: params.reserveNumber,
                    roomReservationNumber:
                      modifyReservationInput.roomStays[roomIndex]
                        .roomReservationNumber
                  }
                })
              }}>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                className={classes.redText}>
                {getTranslate(isMobile ? 'Cancel Room' : 'Cancel This Room')}
              </Typography>
              {loadingForCancelRoomStayPreview && (
                <CircularProgress
                  className={classes.circularProgress}
                  size={isMobile ? 12 : 16}
                />
              )}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              className={classes.btn}
              disabled={
                !modifyReservationInput.roomStays[roomIndex].canModify ||
                !selectedOption ||
                options.length == 0 ||
                loadingForModifyReserve ||
                loadingForSearch
              }
              onClick={() => {
                const { channelID, canCancel, canModify, ...rawInput } =
                  modifyReservationInput.roomStays[roomIndex]

                // Remove all keys starting with "display"
                const input = Object.fromEntries(
                  Object.entries(rawInput).filter(
                    ([key]) => !key.toLowerCase().startsWith('display')
                  )
                )

                modifyReserve({
                  variables: {
                    input,
                    dryRun: 1,
                    reservationNumber: params.reserveNumber
                  }
                })
              }}>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                className={classes.btnText}>
                {getTranslate('Save Changes')}
              </Typography>
              {(loadingForModifyReserve || loadingForSearch) && (
                <CircularProgress
                  className={classes.circularProgress}
                  size={isMobile ? 12 : 16}
                  style={{ color: 'white' }}
                />
              )}
            </Button>
          </div>
        </div>
        <Divider />
        <div className={classes.bottom}>
          <div className={classes.row}>
            <div className={classes.room}>
              <SelectWithLabel
                noHelperText
                label={`${getTranslate('Room')} ${roomIndex + 1}`}
                fullWidth
                disabled={
                  !modifyReservationInput.roomStays[roomIndex].canModify ||
                  loadingForSearch
                }
                size="small"
                inputProps={{
                  style: {
                    direction: lang === 'fa' ? 'rtl' : 'ltr',
                    textAlign: lang === 'fa' ? 'right' : 'left'
                  }
                }}
                value={
                  loadingForSearch
                    ? 'loading'
                    : options.length === 0
                    ? 'no room found'
                    : selectedOption?.displayRoomTypeCode! +
                        selectedOption?.displayRatePlanCode || ''
                }
                onChange={(e) => {
                  const selectedRoomAndRatplan = e.target.value
                  const selected = options.find(
                    (opt) =>
                      opt.displayRoomTypeCode + opt.displayRatePlanCode ===
                      selectedRoomAndRatplan
                  )
                  if (selected) {
                    setSelectedOption(selected)
                  }
                }}
                error={selectedOption === undefined && options.length > 0}>
                {loadingForSearch ? (
                  <MenuItem value="loading" disabled>
                    <div className={classes.circularContainer}>
                      <CircularProgress
                        className={classes.circularProgress}
                        size={14}
                        style={{ margin: '0 8px' }}
                      />
                      <Typography className={classes.circularText}>
                        {getTranslate('Loading...')}
                      </Typography>
                    </div>
                  </MenuItem>
                ) : options.length === 0 ? (
                  <MenuItem disabled value="no room found">
                    {getTranslate(
                      'No Room And RatePlan Found For Selected Date Range'
                    )}
                  </MenuItem>
                ) : (
                  options.map((item, index) => (
                    <MenuItem
                      key={index}
                      value={
                        item.displayRoomTypeCode + item.displayRatePlanCode
                      }>
                      {`${item.roomTypeName} (${item.displayRatePlanCode})`}
                    </MenuItem>
                  ))
                )}
              </SelectWithLabel>
            </div>
            <div ref={datePickerRef}>
              <Typography
                variant="h5"
                className={classes.checkText}>{`${getTranslate(
                'Checkin'
              )} - ${getTranslate('Checkout')}`}</Typography>
              <fieldset
                disabled={
                  !modifyReservationInput.roomStays[roomIndex].canModify
                }
                className={classes.TimePeriodFormContainer}>
                <TimePeriodForm
                  isOpen={arrivalAndDepartureSelected}
                  setTimePickerSelected={setArrivalAndDepartureSelected}
                  dateObject={dateObjectArrivalDeparture}
                  onChange={(data: DateObject) => {
                    setDateObjectArrivalDeparture(data)
                  }}
                  toggleDatePicker={() => {
                    setOnDoneClickForCloseDatePicker(true)
                  }}
                  onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
                  setOnDoneClickForCloseDatePicker={
                    setOnDoneClickForCloseDatePicker
                  }
                  disableBefore={
                    lang === 'fa'
                      ? convertGregorianToJalali(getTodayDate())
                      : getTodayDate()
                  }
                  enableAllDays
                  lightVersion
                />
              </fieldset>
            </div>
            <div className={classes.cell}>
              <Typography variant="h4" className={classes.cellText}>
                {isMobile}
                {getTranslate(isMobile ? 'Guests' : 'Number of Guests')}
              </Typography>
              <CustomCounter
                setValue={setGuestCount}
                value={guestCount}
                min={1}
                smallSize
                disable={!modifyReservationInput.roomStays[roomIndex].canModify}
              />
            </div>
            {selectedOption && (
              <div className={classes.cell}>
                <Typography variant="h5" className={classes.cellText2}>
                  {selectedOption.adults &&
                    `${getTranslate('Adults')}: ${selectedOption?.adults}`}
                </Typography>
                <Typography variant="h5" className={classes.cellText2}>
                  {selectedOption?.extraBeds > 0 &&
                    ` ${getTranslate('Extra Bed')}: ${
                      selectedOption.extraBeds
                    }`}
                </Typography>
              </div>
            )}
          </div>
          {modifyReservationInput.roomStays[roomIndex].profiles.map(
            (_, profileIndex) => (
              <>
                <Divider className={classes.divider} />
                <ProfileForm
                  modifyReservationInput={modifyReservationInput}
                  setModifyReservationInput={setModifyReservationInput}
                  roomIndex={roomIndex}
                  profileIndex={profileIndex}
                  disabled={
                    !modifyReservationInput.roomStays[roomIndex].canModify
                  }
                />
              </>
            )
          )}
          {selectedOption?.availableAddOnes &&
            selectedOption.availableAddOnes.length > 0 && (
              <AddOn
                roomIndex={roomIndex}
                data={selectedOption.availableAddOnes}
                modifyReservationInput={modifyReservationInput}
                setModifyReservationInput={setModifyReservationInput}
              />
            )}
        </div>
      </div>
      <ConfirmChangeReservationModal
        isOpen={modalOpen}
        mode={mode}
        onClose={() => setModalOpen(false)}
        data={modalData}
        loading={loadingForModifyReserve}
        onClick={() => {
          if (mode === 'cancelation') {
            history.push(
              `/${lang}/booking/list/${params.reserveNumber}/edit/cancel-room-stay/${modifyReservationInput.roomStays[roomIndex].roomReservationNumber}`
            )
          } else {
            const { channelID, canCancel, canModify, ...rawInput } =
              modifyReservationInput.roomStays[roomIndex]
            // Remove all keys starting with "display"
            const input = Object.fromEntries(
              Object.entries(rawInput).filter(
                ([key]) => !key.toLowerCase().startsWith('display')
              )
            )
            modifyReserve({
              variables: {
                input,
                dryRun: 0,
                reservationNumber: params.reserveNumber
              }
            }).then(({ data }) => {
              if (data) {
                history.push(
                  `/${lang}/booking/list/${params.reserveNumber}/edit/modify-room-stay/${modifyReservationInput.roomStays[roomIndex].roomReservationNumber}`
                )
              }
            })
          }
        }}
      />
    </>
  )
}

export default RoomStayInformation
